import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';

export type VhImageAspect = '1/1' | '4/3' | '16/9' | '3/4' | '3/2';
export type VhImageFit = 'cover' | 'contain';

/**
 * Responsive image with built-in skeleton, error fallback, and aspect-
 * ratio box so layout never jumps. Pass a CDN or Picsum URL; the
 * component reserves the slot via `aspect-ratio` CSS.
 */
@Component({
  selector: 'vh-image',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (!errored()) {
      <img
        [src]="src()"
        [alt]="alt()"
        [loading]="loading()"
        [attr.decoding]="'async'"
        (load)="onLoad()"
        (error)="onError()"
      />
    }
    @if (!loaded() && !errored()) {
      <div class="vh-image__skeleton" aria-hidden="true"></div>
    }
    @if (errored()) {
      <div class="vh-image__fallback" aria-hidden="true">
        <svg
          viewBox="0 0 24 24"
          width="28"
          height="28"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="9" cy="9" r="2" />
          <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
        </svg>
      </div>
    }
  `,
  styleUrl: './image.component.scss',
  host: {
    '[style.aspect-ratio]': 'aspectRatioCss()',
    '[class.vh-image--contain]': 'fit() === "contain"',
  },
})
export class VhImageComponent {
  readonly src = input.required<string>();
  readonly alt = input<string>('');
  readonly aspectRatio = input<VhImageAspect>('4/3');
  readonly fit = input<VhImageFit>('cover');
  readonly loading = input<'lazy' | 'eager'>('lazy');

  protected readonly loaded = signal(false);
  protected readonly errored = signal(false);

  readonly aspectRatioCss = computed(() => this.aspectRatio().replace('/', ' / '));

  onLoad(): void {
    this.loaded.set(true);
  }

  onError(): void {
    this.errored.set(true);
    this.loaded.set(true);
  }
}
